/**
 * Bilingual language toggle. Place in the header of every page.
 * - Reads current path
 * - Looks up its EN<->ES counterpart from /app/frontend/src/i18n/routeMap.js
 * - Navigates to the equivalent page in the other language
 * - Persists user preference in localStorage so subsequent visits land on
 *   their preferred language directly (server-side hreflang handles bots).
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { toEn, toEs } from '@/i18n/routeMap';

const LangToggle = ({ className = '', compact = false }) => {
  const loc = useLocation();
  const nav = useNavigate();
  const isEs = loc.pathname === '/es' || loc.pathname.startsWith('/es/');

  const flip = () => {
    const target = isEs ? toEn(loc.pathname) : toEs(loc.pathname);
    try {
      window.localStorage.setItem('fwd_lang_pref', isEs ? 'en' : 'es');
    } catch { /* ignore quota / private mode */ }
    nav(target);
  };

  const label = isEs ? 'English' : 'Español';
  const aria = isEs ? 'Switch to English' : 'Cambiar a Español';

  if (compact) {
    return (
      <button
        type="button"
        onClick={flip}
        aria-label={aria}
        data-testid="lang-toggle"
        className={`inline-flex items-center gap-1 text-sm font-semibold hover:text-teal-200 transition-colors ${className}`}
      >
        <Globe className="w-4 h-4" aria-hidden="true" />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={flip}
      aria-label={aria}
      data-testid="lang-toggle"
      className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full border border-white/40 hover:bg-white/10 transition-colors ${className}`}
    >
      <Globe className="w-4 h-4" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
};

export default LangToggle;
